import { useState, useEffect, useCallback } from 'react';
import { Check, Minus, Plus, Calendar, Flame, Droplets, Target, Zap, GlassWater, Scale, ChevronLeft, ChevronRight } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { ProgressRing } from '../components/ProgressRing';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  getSettings, 
  getDailyLog, 
  saveDailyLog, 
  getWeightLog,
  getAllWeightLogs,
  saveWeightLog,
  getMealsByDate,
  saveMealLog
} from '../lib/db';
import { 
  getDayType, 
  CHECKLIST_ITEMS, 
  WORKOUT_TEMPLATE,
  getDateString, 
  formatDate 
} from '../lib/constants';
import { calculateProgress, getDaysRemaining, getCurrentDay } from '../lib/utils';

// Whey shake constants - ON Gold Standard
const WHEY_SHAKE = {
  id: 'whey-shake-quick',
  name: 'Whey Shake (ON Gold Standard)',
  shortName: 'Whey Shake',
  calories: 120,
  protein: 24, // per scoop
  category: 'shake',
};

export const Dashboard = () => {
  const [settings, setSettings] = useState(null);
  const [dailyLog, setDailyLog] = useState(null);
  const [currentWeight, setCurrentWeight] = useState(null);
  const [newWeight, setNewWeight] = useState('');
  const [showWeightInput, setShowWeightInput] = useState(false);
  const [shakeCount, setShakeCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const today = getDateString(selectedDate);
  const isToday = getDateString(selectedDate) === getDateString(new Date());

  const navigateDay = (delta) => {
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });
  };

  const loadData = useCallback(async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);

      const log = await getDailyLog(today);
      const dayType = getDayType(today, settingsData.startDate);
      
      if (log) {
        setDailyLog(log);
      } else {
        // Create new daily log
        const newLog = {
          date: today,
          dayType,
          checklist: {},
          calories: 0,
          protein: 0,
          water: 0,
        };
        setDailyLog(newLog);
        await saveDailyLog(newLog);
      }

      // Get latest weight
      const weights = await getAllWeightLogs();
      if (weights.length > 0) {
        const sorted = weights.sort((a, b) => new Date(b.date) - new Date(a.date));
        setCurrentWeight(sorted[0].weight);
      } else {
        setCurrentWeight(settingsData.startWeight);
      }

      // Get today's shake count
      const todayMeals = await getMealsByDate(today);
      const shakes = todayMeals.filter(m => 
        m.category === 'shake' || 
        m.templateId === 'snack-whey' || 
        m.templateId === 'whey-shake-quick'
      );
      setShakeCount(shakes.length);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reload when selected date changes
  useEffect(() => {
    setLoading(true);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const toggleChecklist = async (itemId) => {
    if (!dailyLog) return;
    
    const updated = {
      ...dailyLog,
      checklist: {
        ...dailyLog.checklist,
        [itemId]: dailyLog.checklist[itemId] 
          ? null 
          : new Date().toISOString(),
      },
    };
    setDailyLog(updated);
    await saveDailyLog(updated);
  };

  const updateMacro = async (field, delta) => {
    if (!dailyLog) return;
    
    const newValue = Math.max(0, (dailyLog[field] || 0) + delta);
    const updated = { ...dailyLog, [field]: newValue };
    setDailyLog(updated);
    await saveDailyLog(updated);
  };

  const logWheyShake = async () => {
    // Log the shake as a meal
    const meal = {
      id: uuidv4(),
      date: today,
      templateId: 'whey-shake-quick',
      name: WHEY_SHAKE.name,
      shortName: WHEY_SHAKE.shortName,
      calories: WHEY_SHAKE.calories,
      protein: WHEY_SHAKE.protein,
      category: 'shake',
      timestamp: new Date().toISOString(),
    };
    
    await saveMealLog(meal);
    setShakeCount(prev => prev + 1);
    
    // Also update macros
    const updated = { 
      ...dailyLog, 
      calories: (dailyLog.calories || 0) + WHEY_SHAKE.calories,
      protein: (dailyLog.protein || 0) + WHEY_SHAKE.protein,
    };
    setDailyLog(updated);
    await saveDailyLog(updated);
    
    toast.success(`+${WHEY_SHAKE.protein}g protein! 💪`);
  };

  const logWeight = async () => {
    const weight = parseFloat(newWeight);
    if (isNaN(weight) || weight < 30 || weight > 300) {
      toast.error('Enter a valid weight (30-300 kg)');
      return;
    }

    await saveWeightLog({ date: today, weight });
    setCurrentWeight(weight);
    setNewWeight('');
    setShowWeightInput(false);
    toast.success(`Weight updated: ${weight} kg`);
  };

  if (loading || !settings || !dailyLog) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const dayType = getDayType(today, settings.startDate);
  const checklist = dayType === 'cheat' 
    ? CHECKLIST_ITEMS.cheatDay 
    : dayType === 'gym' 
      ? CHECKLIST_ITEMS.gymDay 
      : CHECKLIST_ITEMS.walkDay;

  const completedCount = Object.values(dailyLog.checklist).filter(Boolean).length;
  const progress = calculateProgress(currentWeight, settings.startWeight, settings.targetWeight);
  const kgLost = settings.startWeight - currentWeight;
  const kgTarget = settings.startWeight - settings.targetWeight;
  const daysRemaining = getDaysRemaining(settings.startDate, settings.durationDays);
  const currentDay = getCurrentDay(settings.startDate);

  const isCheatDay = dayType === 'cheat';

  return (
    <div className={`space-y-6 ${isCheatDay ? 'cheat-mode' : ''}`} data-testid="dashboard">
      {/* Header with Day Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateDay(-1)}
          className="h-10 w-10 rounded-xl hover:bg-white/10"
        >
          <ChevronLeft size={20} />
        </Button>
        <div className="text-center flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
            {isToday ? `Day ${currentDay} of ${settings.durationDays}` : formatDate(today)}
          </p>
          <h1 className="text-2xl font-black tracking-tight">
            {isToday ? 'Today' : formatDate(today)}
          </h1>
          {!isToday && (
            <button 
              onClick={() => setSelectedDate(new Date())}
              className="text-[10px] text-[#00E676] font-bold uppercase tracking-wider mt-1"
            >
              ← Back to Today
            </button>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigateDay(1)}
          className="h-10 w-10 rounded-xl hover:bg-white/10"
        >
          <ChevronRight size={20} />
        </Button>
      </div>
      
      {/* Day Type Badge */}
      <div className="flex justify-center">
        <div className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider ${
          isCheatDay 
            ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' 
            : dayType === 'gym' 
              ? 'bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30'
              : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
        }`}>
          {isCheatDay ? '🔥 Cheat Day' : dayType === 'gym' ? '💪 Gym Day' : '🚶 Walk Day'}
        </div>
      </div>

      {/* Cheat Day Banner */}
      {isCheatDay && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4" data-testid="cheat-banner">
          <p className="text-orange-400 font-bold text-sm">
            ⚡ Remember: 60 air squats + ½ tsp cinnamon before each meal
          </p>
        </div>
      )}

      {/* Progress Ring Card */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-6" data-testid="progress-card">
        <div className="flex items-center justify-center">
          <ProgressRing progress={progress} size={180} strokeWidth={10}>
            <div className="text-center">
              <p className="text-4xl font-black font-mono text-[#00E676]">
                {kgLost.toFixed(1)}
              </p>
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500">
                of {kgTarget}kg lost
              </p>
            </div>
          </ProgressRing>
        </div>
        
        {/* Stats Row - Tappable Current Weight */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <button 
            onClick={() => setShowWeightInput(true)}
            className="text-center p-2 -m-2 rounded-xl hover:bg-white/5 active:scale-95 transition-all"
            data-testid="current-weight-btn"
          >
            <p className="text-2xl font-black font-mono">{currentWeight}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Current kg</p>
            <p className="text-[8px] text-[#00E676] mt-1">Tap to update</p>
          </button>
          <div className="text-center">
            <p className="text-2xl font-black font-mono text-[#00E676]">
              {kgLost > 0 ? '-' : ''}{Math.abs(kgLost).toFixed(1)}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">From start</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-black font-mono">{daysRemaining}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Days left</p>
          </div>
        </div>
      </div>

      {/* Weight Input Panel */}
      {showWeightInput && (
        <div className="bg-[#24243e] border border-[#00E676]/30 rounded-2xl p-5" data-testid="weight-input-panel">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#00E676]/20 flex items-center justify-center">
              <Scale className="text-[#00E676]" size={20} />
            </div>
            <div>
              <p className="font-bold">Update Weight</p>
              <p className="text-xs text-gray-500">Log today's weigh-in</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Input
              type="number"
              inputMode="decimal"
              placeholder={`Current: ${currentWeight} kg`}
              value={newWeight}
              onChange={(e) => setNewWeight(e.target.value)}
              className="flex-1 bg-[#161625] border-white/10 font-mono text-lg text-center"
              autoFocus
              data-testid="weight-input-field"
            />
            <Button 
              onClick={logWeight}
              className="bg-[#00E676] text-black font-bold hover:bg-[#00c853] px-6"
              data-testid="save-weight-btn"
            >
              Save
            </Button>
            <Button 
              onClick={() => { setShowWeightInput(false); setNewWeight(''); }}
              variant="outline"
              className="border-white/10"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Quick Log Row */}
      <div className="grid grid-cols-3 gap-3" data-testid="quick-log">
        <QuickLogCard
          icon={<Flame className="text-orange-400" size={18} />}
          value={dailyLog.calories}
          target={settings.targetCalories}
          unit="cal"
          onIncrement={() => updateMacro('calories', 100)}
          onDecrement={() => updateMacro('calories', -100)}
        />
        <QuickLogCard
          icon={<Target className="text-[#00E676]" size={18} />}
          value={dailyLog.protein}
          target={settings.targetProtein}
          unit="g"
          onIncrement={() => updateMacro('protein', 10)}
          onDecrement={() => updateMacro('protein', -10)}
        />
        <QuickLogCard
          icon={<Droplets className="text-blue-400" size={18} />}
          value={dailyLog.water}
          target={settings.targetWater}
          unit="L"
          onIncrement={() => updateMacro('water', 0.5)}
          onDecrement={() => updateMacro('water', -0.5)}
        />
      </div>

      {/* Protein Shake Quick-Log - Front & Center */}
      <div 
        className="bg-gradient-to-r from-[#00E676]/10 to-[#00c853]/5 border border-[#00E676]/30 rounded-2xl p-4"
        data-testid="shake-tracker"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#00E676]/20 flex items-center justify-center">
              <GlassWater className="text-[#00E676]" size={24} />
            </div>
            <div>
              <p className="font-bold text-white">Whey Shake</p>
              <p className="text-xs text-gray-400">ON Gold Standard • 24g protein</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-2xl font-black font-mono text-[#00E676]">{shakeCount}</p>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">of 2</p>
            </div>
            <Button
              onClick={logWheyShake}
              className="h-14 w-14 rounded-xl bg-[#00E676] hover:bg-[#00c853] text-black font-black text-xl shadow-lg shadow-[#00E676]/20"
              data-testid="log-shake-btn"
            >
              +1
            </Button>
          </div>
        </div>
        {shakeCount >= 2 && (
          <div className="mt-3 pt-3 border-t border-[#00E676]/20 flex items-center gap-2">
            <Check size={16} className="text-[#00E676]" />
            <span className="text-xs text-[#00E676] font-bold">Shake target hit!</span>
          </div>
        )}
        {shakeCount === 0 && (
          <p className="mt-3 text-xs text-gray-500">
            Easiest way to hit 180g — just tap to log
          </p>
        )}
      </div>

      {/* Tomorrow's Preview */}
      {isToday && (() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowType = getDayType(getDateString(tomorrow), settings.startDate);
        const tomorrowLabel = tomorrowType === 'gym' ? '💪 Gym Day' : tomorrowType === 'cheat' ? '🔥 Cheat Day' : '🚶 Walk Day';
        
        return (
          <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5" data-testid="tomorrow-preview">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-black uppercase tracking-wider">Tomorrow</h2>
              <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${
                tomorrowType === 'gym' ? 'bg-[#00E676]/20 text-[#00E676]' : 
                tomorrowType === 'cheat' ? 'bg-orange-500/20 text-orange-400' : 
                'bg-blue-500/20 text-blue-400'
              }`}>{tomorrowLabel}</span>
            </div>
            {tomorrowType === 'gym' ? (
              <div className="space-y-2">
                {/* Group by superset pairs */}
                {(() => {
                  const pairs = [];
                  const used = new Set();
                  WORKOUT_TEMPLATE.forEach(ex => {
                    if (used.has(ex.id)) return;
                    used.add(ex.id);
                    if (ex.supersetWith) {
                      const partner = WORKOUT_TEMPLATE.find(e => e.id === ex.supersetWith);
                      if (partner) used.add(partner.id);
                      pairs.push({ a: ex, b: partner });
                    } else {
                      pairs.push({ a: ex, b: null });
                    }
                  });
                  return pairs.map((pair, i) => (
                    <div key={i} className="bg-[#161625] rounded-xl p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-bold text-white">
                            {pair.a.name}
                            {pair.b ? <span className="text-gray-500"> ↔ </span> : null}
                            {pair.b ? pair.b.name : null}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {pair.a.sets}×{pair.a.reps}
                            {pair.b ? ` / ${pair.b.sets}×${pair.b.reps}` : null}
                            {' • '}{pair.a.restTime}s rest
                          </p>
                        </div>
                        {pair.a.warning && (
                          <span className="text-[10px] text-orange-400">⚠️ Knee</span>
                        )}
                      </div>
                    </div>
                  ));
                })()}
                <p className="text-[10px] text-gray-500 text-center mt-2">~45-50 min • Superset pairs • Log weights in app</p>
              </div>
            ) : tomorrowType === 'cheat' ? (
              <p className="text-sm text-orange-400">Eat whatever you want. 60 air squats + ½ tsp cinnamon before each meal.</p>
            ) : (
              <p className="text-sm text-blue-400">Walk 10K+ steps. Active recovery day.</p>
            )}
          </div>
        );
      })()}

      {/* Checklist */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-5" data-testid="checklist">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-black uppercase tracking-wider">Today's Checklist</h2>
          <span className="text-xs font-bold text-gray-500">
            {completedCount}/{checklist.length}
          </span>
        </div>
        
        <div className="space-y-3">
          {checklist.map((item) => {
            const isChecked = !!dailyLog.checklist[item.id];
            const timestamp = dailyLog.checklist[item.id];
            
            return (
              <button
                key={item.id}
                onClick={() => toggleChecklist(item.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all active:scale-[0.98] ${
                  isChecked 
                    ? 'bg-[#00E676]/10 border border-[#00E676]/30' 
                    : 'bg-[#161625] border border-white/5 hover:border-white/10'
                }`}
                data-testid={`checklist-${item.id}`}
              >
                <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                  isChecked 
                    ? 'bg-[#00E676] text-black' 
                    : 'border-2 border-gray-600'
                }`}>
                  {isChecked && <Check size={14} strokeWidth={3} />}
                </div>
                <span className={`flex-1 text-left text-sm font-medium ${
                  isChecked ? 'text-white' : 'text-gray-300'
                }`}>
                  {item.label}
                </span>
                {timestamp && (
                  <span className="text-[10px] font-mono text-gray-500">
                    {new Date(timestamp).toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const QuickLogCard = ({ icon, value, target, unit, onIncrement, onDecrement }) => {
  const percentage = Math.min((value / target) * 100, 100);
  const isComplete = value >= target;

  return (
    <div 
      className={`bg-[#24243e] border rounded-xl p-3 ${
        isComplete ? 'border-[#00E676]/30' : 'border-white/5'
      }`}
      data-testid="quick-log-card"
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className={`text-lg font-black font-mono ${isComplete ? 'text-[#00E676]' : 'text-white'}`}>
          {value}
        </span>
        <span className="text-xs text-gray-500">/{target}{unit}</span>
      </div>
      
      {/* Mini progress bar */}
      <div className="h-1 bg-[#161625] rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full transition-all ${isComplete ? 'bg-[#00E676]' : 'bg-gray-500'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      <div className="flex gap-1">
        <Button
          size="icon"
          variant="ghost"
          onClick={onDecrement}
          className="h-8 w-8 bg-[#161625] hover:bg-[#1a1a2e]"
          data-testid="quick-log-decrement"
        >
          <Minus size={14} />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={onIncrement}
          className="h-8 w-8 bg-[#161625] hover:bg-[#1a1a2e]"
          data-testid="quick-log-increment"
        >
          <Plus size={14} />
        </Button>
      </div>
    </div>
  );
};

export default Dashboard;
